import { useState, useRef, useEffect } from 'react'
import { usePolicy } from '../../../contexts/PolicyContext'
import { useAssessment } from '../../../contexts/AssessmentContext'
import { useAuth } from '../../../contexts/AuthContext'
import { useOrg } from '../../../contexts/OrgContext'
import { useDemoPlan } from '../../../hooks/useDemoPlan'
import { supabase } from '../../../shared/lib/supabase'
import { exportPolicyToPdf } from '../../../shared/utils/policyExport'
import { exportStructuredPolicyToPdf } from '../utils/policyExport'
import { exportPolicyAsDocx } from '../utils/policyExport'
import PolicySettingsPanel from '../components/PolicySettingsPanel'
import PolicyPreview from '../components/PolicyPreview'
import TemplateUpdateReviewModal from '../components/TemplateUpdateReviewModal'
import SectionEditorPanel from '../components/SectionEditorPanel'
import GlobalTemplatePolicyPreview from '../../global-admin/components/GlobalTemplatePolicyPreview'
import StructuredPolicyRenderer from '../../../shared/components/StructuredPolicyRenderer'
import BrandingApplicationModal from '../components/BrandingApplicationModal'

const DOMAIN_OPTIONS = [
  'Acceptable Use Policy',
  'Access Control Policy',
  'AI & Emerging Technology Policy',
  'Asset Management Policy',
  'Backup & Recovery Policy',
  'Business Continuity & Disaster Recovery Policy',
  'Change Management Policy',
  'Cloud Security Policy',
  'Data Classification & Handling Policy',
  'Data Retention & Disposal Policy',
  'Encryption & Key Management Policy',
  'Endpoint Security Policy',
  'Incident Response Policy',
  'Information Security Policy',
  'Logging, Monitoring & Audit Policy',
  'Mobile Device & BYOD Policy',
  'Network Security Policy',
  'Password & Authentication Policy',
  'Physical Security Policy',
  'Privacy Policy',
  'Remote Work & Telework Policy',
  'Risk Management Policy',
  'Secure Software Development Policy',
  'Security Awareness & Training Policy',
  'Third-Party / Vendor Management Policy',
  'Vulnerability Management Policy'
]

const FRAMEWORK_OPTIONS = [
  'NIST CSF', 'NIST 800-53', 'NIST 800-171', 'HIPAA', 'PCI DSS',
  'SOC 2', 'CMMC', 'ISO 27001', 'GDPR', 'CCPA', 'CIS Controls',
  'FFIEC', 'GLBA', 'FERPA', 'FTC Safeguards Rule', 'NYDFS',
  'SEC Cybersecurity Rules', 'DORA'
]

const INDUSTRY_OPTIONS = [
  'Healthcare', 'Financial Services', 'Legal', 'Manufacturing',
  'Technology', 'Retail', 'Education', 'Government', 'Nonprofit',
  'Real Estate', 'Professional Services', 'Energy & Utilities',
  'Transportation & Logistics'
]

function relativeTime(dateStr) {
  const now = new Date()
  const date = new Date(dateStr)
  const diffMs = now - date
  const diffMin = Math.floor(diffMs / 60000)
  const diffHr = Math.floor(diffMs / 3600000)
  const diffDay = Math.floor(diffMs / 86400000)

  if (diffMin < 1) return 'just now'
  if (diffMin < 60) return `${diffMin}m ago`
  if (diffHr < 24) return `${diffHr}h ago`
  if (diffDay < 30) return `${diffDay}d ago`
  return date.toLocaleDateString()
}

function StructuredContentPreview({ content, policy }) {
  if (!content) return <p className="text-sm text-gray-400 italic">No structured content</p>

  return (
    <div className="bg-white shadow-lg" style={{ minHeight: '600px' }}>
      <StructuredPolicyRenderer
        structuredContent={content}
        template={policy || {}}
        showRevisionHistory={false}
      />
    </div>
  )
}

function PolicyLibrary() {
  const { templates, globalTemplates: allGlobalTemplates, loading, createTemplate, updateTemplate, deleteTemplate, copyToClient, fetchTemplates } = usePolicy()
  const globalTemplates = allGlobalTemplates.filter(t => t.status === 'published')
  const { clients } = useAssessment()
  const { user, profile } = useAuth()
  const { currentOrg, userOrgRole } = useOrg() || {}
  const { isDemoOrg, demoMessage } = useDemoPlan()
  const isReadOnly = userOrgRole === 'org_member' || profile?.role === 'org_member'
  const [demoNotice, setDemoNotice] = useState('')

  const checkDemoBlock = (feature) => {
    if (isDemoOrg) {
      setDemoNotice(demoMessage(feature))
      setTimeout(() => setDemoNotice(''), 8000)
      return true
    }
    return false
  }

  // Tab state
  const [activeTab, setActiveTab] = useState('org')

  // Global template browse state (read-only for orgs)
  const [viewingGlobalTemplate, setViewingGlobalTemplate] = useState(null)
  const [copySuccess, setCopySuccess] = useState('')

  // Update notification state
  const [pendingUpdates, setPendingUpdates] = useState([])
  const [bannerDismissed, setBannerDismissed] = useState(false)
  const [showUpdateReview, setShowUpdateReview] = useState(false)
  const [singleReviewTemplate, setSingleReviewTemplate] = useState(null)

  // Filter & sort state
  const [search, setSearch] = useState('')
  const [domainFilter, setDomainFilter] = useState('')
  const [frameworkFilter, setFrameworkFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [sortField, setSortField] = useState('domain')
  const [sortDirection, setSortDirection] = useState('asc')

  // Modal state
  const [showModal, setShowModal] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null)
  const [showCopyDropdown, setShowCopyDropdown] = useState(null)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [successMessage, setSuccessMessage] = useState('')
  const [showSettings, setShowSettings] = useState(false)
  const [viewMode, setViewMode] = useState('edit')
  const [showExportMenu, setShowExportMenu] = useState(false)
  const [showPreviewModal, setShowPreviewModal] = useState(false)
  const [previewPolicy, setPreviewPolicy] = useState(null)
  const [showPreviewExportMenu, setShowPreviewExportMenu] = useState(false)
  const [generateError, setGenerateError] = useState('')

  // Upload revision state
  const [showUploadRevision, setShowUploadRevision] = useState(false)
  const [uploadRevisionTemplate, setUploadRevisionTemplate] = useState(null)
  const [uploadFile, setUploadFile] = useState(null)
  const [uploadChangeSummary, setUploadChangeSummary] = useState('')
  const [uploading, setUploading] = useState(false)

  // Section editor & branding state
  const [sectionEditorTemplate, setSectionEditorTemplate] = useState(null)
  const [showBrandingModal, setShowBrandingModal] = useState(false)
  const [orgBrandConfig, setOrgBrandConfig] = useState(null)

  // Form state
  const [formTitle, setFormTitle] = useState('')
  const [formDomain, setFormDomain] = useState('')
  const [formContent, setFormContent] = useState('')
  const [formFrameworkTags, setFormFrameworkTags] = useState([])
  const [formIndustryTags, setFormIndustryTags] = useState([])
  const [formStatus, setFormStatus] = useState('draft')

  const copyDropdownRef = useRef(null)

  // Close copy dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e) {
      if (copyDropdownRef.current && !copyDropdownRef.current.contains(e.target)) {
        setShowCopyDropdown(null)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Auto-clear success messages
  useEffect(() => {
    if (successMessage) {
      const t = setTimeout(() => setSuccessMessage(''), 3000)
      return () => clearTimeout(t)
    }
  }, [successMessage])

  // Fetch org brand config
  useEffect(() => {
    if (!currentOrg?.id) return
    supabase.from('org_brand_config').select('*').eq('org_id', currentOrg.id).maybeSingle()
      .then(({ data }) => setOrgBrandConfig(data))
  }, [currentOrg?.id])

  // Fetch pending template updates
  const loadPendingUpdates = async () => {
    if (!currentOrg?.id) return
    const { data } = await supabase
      .from('org_template_copies')
      .select('*, policy_templates!org_template_id(*)')
      .eq('org_id', currentOrg.id)
      .eq('status', 'update_available')
    setPendingUpdates((data || []).map(d => ({
      ...d.policy_templates,
      org_template_copies_record: { id: d.id, global_template_id: d.global_template_id, global_version_at_copy: d.global_version_at_copy, status: d.status }
    })).filter(Boolean))
  }

  useEffect(() => {
    loadPendingUpdates()
  }, [currentOrg?.id])

  // Filter logic
  const hasActiveFilters = search || domainFilter || frameworkFilter || statusFilter
  const filtered = templates.filter(t => {
    if (search) {
      const q = search.toLowerCase()
      if (!t.title.toLowerCase().includes(q) && !t.domain.toLowerCase().includes(q)) return false
    }
    if (domainFilter && t.domain !== domainFilter) return false
    if (frameworkFilter && !(t.framework_tags || []).includes(frameworkFilter)) return false
    if (statusFilter && t.status !== statusFilter) return false
    return true
  })

  // Sort filtered results
  const sorted = [...filtered].sort((a, b) => {
    let aVal, bVal
    switch (sortField) {
      case 'title': aVal = a.title.toLowerCase(); bVal = b.title.toLowerCase(); break
      case 'domain': aVal = a.domain.toLowerCase(); bVal = b.domain.toLowerCase(); break
      case 'status': aVal = a.status; bVal = b.status; break
      case 'updated': aVal = new Date(a.updated_at).getTime(); bVal = new Date(b.updated_at).getTime(); break
      default: aVal = a.domain.toLowerCase(); bVal = b.domain.toLowerCase()
    }
    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1
    return 0
  })

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const SortIndicator = ({ field }) => {
    if (sortField !== field) return null
    return (
      <svg className="w-3 h-3 inline ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        {sortDirection === 'asc'
          ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
          : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        }
      </svg>
    )
  }

  const openCreateModal = () => {
    setEditingTemplate(null)
    setFormTitle('')
    setFormDomain('')
    setFormContent('')
    setFormFrameworkTags([])
    setFormIndustryTags([])
    setFormStatus('draft')
    setSaveError('')
    setGenerateError('')
    setViewMode('edit')
    setShowExportMenu(false)
    setShowModal(true)
  }

  const openEditModal = (template) => {
    setEditingTemplate(template)
    setFormTitle(template.title)
    setFormDomain(template.domain)
    setFormContent(template.content || '')
    setFormFrameworkTags(template.framework_tags || [])
    setFormIndustryTags(template.industry_tags || [])
    setFormStatus(template.status)
    setSaveError('')
    setGenerateError('')
    setShowCopyDropdown(null)
    setViewMode(template.content ? 'preview' : 'edit')
    setShowExportMenu(false)
    setShowModal(true)
  }

  const closeModal = () => {
    setShowModal(false)
    setEditingTemplate(null)
    setShowCopyDropdown(null)
    setSaveError('')
    setShowExportMenu(false)
  }

  const handleSave = async (e) => {
    e.preventDefault()
    setSaving(true)
    setSaveError('')
    try {
      const payload = {
        title: formTitle,
        domain: formDomain,
        content: formContent,
        framework_tags: formFrameworkTags,
        industry_tags: formIndustryTags,
        status: formStatus,
        // Scope new templates to the org the user is currently viewing.
        // Required by the org_id-scoped RLS policy on policy_templates;
        // omitting it produces a row invisible to org_admins.
        org_id: currentOrg?.id
      }
      if (editingTemplate) {
        await updateTemplate(editingTemplate.id, payload)
      } else {
        await createTemplate(payload)
      }
      closeModal()
    } catch (err) {
      console.error('Error saving template:', err)
      setSaveError(err.message || 'Failed to save template')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id) => {
    try {
      await deleteTemplate(id)
      setShowDeleteConfirm(null)
    } catch (err) {
      console.error('Error deleting template:', err)
    }
  }

  const handleCopyToClient = async (templateId, client) => {
    try {
      await copyToClient(templateId, client.id)
      setShowCopyDropdown(null)
      setSuccessMessage(`Policy copied to ${client.name}`)
    } catch (err) {
      console.error('Error copying to client:', err)
    }
  }

  const handleCopyGlobalToOrg = async (template) => {
    if (!template) return
    if (!currentOrg?.id) {
      console.error('Cannot copy global template — no current org selected')
      return
    }
    try {
      const newTemplate = await createTemplate({
        title: template.title.replace(' — Global Template', '') + ' (from Global)',
        domain: template.domain,
        content: template.content || '',
        framework_tags: template.framework_tags,
        industry_tags: template.industry_tags,
        status: 'draft',
        global_template: false,
        structured_content: template.structured_content,
        generation_model: template.generation_model,
        source_global_template_id: template.id,
        // Scope the copy to the org the user is currently viewing —
        // required by org_id-scoped RLS on policy_templates.
        org_id: currentOrg.id
      })

      // Track the copy so version update notifications work
      if (newTemplate?.id && currentOrg?.id && template.id) {
        await supabase.from('org_template_copies').upsert({
          org_id: currentOrg.id,
          global_template_id: template.id,
          org_template_id: newTemplate.id,
          global_version_at_copy: template.version_number || '1.0',
          status: 'current'
        }, { onConflict: 'org_id,global_template_id' })
      }

      setCopySuccess(`Copied "${template.domain}" to your org templates`)
      setTimeout(() => setCopySuccess(''), 3000)
    } catch (err) {
      console.error('Error copying global template:', err)
    }
  }

  const handleUploadRevision = async () => {
    if (!uploadFile || !uploadChangeSummary.trim() || !uploadRevisionTemplate) return
    setUploading(true)
    try {
      const filePath = `${currentOrg?.id}/template-revisions/${uploadRevisionTemplate.id}/${Date.now()}.docx`
      const { error: storageError } = await supabase.storage.from('org-assets').upload(filePath, uploadFile)

      if (storageError) {
        if (storageError.message?.includes('bucket') || storageError.message?.includes('not found')) {
          setSuccessMessage('Storage not configured. Contact your platform administrator.')
          return
        }
        throw storageError
      }

      await supabase.from('org_template_revisions').insert({
        template_id: uploadRevisionTemplate.id,
        org_id: currentOrg?.id,
        content: '[Uploaded .docx — see revision file]',
        structured_content: null,
        change_summary: uploadChangeSummary.trim(),
        edit_method: 'docx_upload',
        edited_by: user?.id
      })

      await updateTemplate(uploadRevisionTemplate.id, { org_edited: true })
      setShowUploadRevision(false)
      setUploadRevisionTemplate(null)
      setUploadFile(null)
      setUploadChangeSummary('')
      setSuccessMessage('Revision uploaded and version-controlled.')
    } catch (err) {
      console.error('Upload revision error:', err)
    } finally {
      setUploading(false)
    }
  }

  const toggleFrameworkTag = (tag) => {
    setFormFrameworkTags(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    )
  }

  const toggleIndustryTag = (tag) => {
    setFormIndustryTags(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div>
      {/* Demo mode notice toast */}
      {demoNotice && (
        <div className="fixed top-4 right-4 z-[60] max-w-sm p-4 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 rounded-lg shadow-lg">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm text-amber-800 dark:text-amber-200">{demoNotice}</p>
          </div>
        </div>
      )}

      {/* Success toast */}
      {successMessage && (
        <div className="fixed top-4 right-4 z-50 bg-green-600 text-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-2">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          {successMessage}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Policy Library</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Manage your master policy templates</p>
        </div>
        <div className="flex items-center gap-2">
          {activeTab === 'org' && orgBrandConfig && (orgBrandConfig.logo_url || (orgBrandConfig.primary_color && orgBrandConfig.primary_color !== '#0D1B2A')) && (
            <button
              onClick={() => setShowBrandingModal(true)}
              className="btn-secondary text-sm flex items-center gap-1.5"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.098 19.902a3.75 3.75 0 005.304 0l6.401-6.402M6.75 21A3.75 3.75 0 013 17.25V4.125C3 3.504 3.504 3 4.125 3h5.25c.621 0 1.125.504 1.125 1.125v4.072M6.75 21a3.75 3.75 0 003.75-3.75V8.197M6.75 21h13.125c.621 0 1.125-.504 1.125-1.125v-5.25c0-.621-.504-1.125-1.125-1.125h-4.072M10.5 8.197l2.88-2.88c.438-.439 1.15-.439 1.59 0l3.712 3.713c.44.44.44 1.152 0 1.59l-2.879 2.88M6.75 17.25h.008v.008H6.75v-.008z" />
              </svg>
              Apply Branding
            </button>
          )}
          <button
            onClick={() => setShowSettings(true)}
            className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            title="Policy Generation Settings"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </button>
          {activeTab === 'org' && !isReadOnly && (
            <button onClick={openCreateModal} className="btn-primary flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4.5v15m7.5-7.5h-15" />
              </svg>
              Create Policy
            </button>
          )}
        </div>
      </div>

      {/* Tab navigation */}
      <div className="flex gap-1 mb-6 border-b border-gray-200 dark:border-gray-700">
        <button
          onClick={() => setActiveTab('org')}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'org'
              ? 'border-primary-600 text-primary-600 dark:text-accent-300 dark:border-accent-300'
              : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          My Library
          <span className="ml-1.5 text-xs px-1.5 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400">{templates.length}</span>
        </button>
        <button
          onClick={() => setActiveTab('global')}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'global'
              ? 'border-primary-600 text-primary-600 dark:text-accent-300 dark:border-accent-300'
              : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          Global Templates
          {(() => {
            const unadopted = globalTemplates.filter(gt => !templates.some(t => t.source_global_template_id === gt.id)).length
            const updates = pendingUpdates.length
            const actionCount = unadopted + updates
            if (actionCount > 0) {
              return <span className="ml-1.5 text-xs px-1.5 py-0.5 rounded-full bg-amber-500 text-white font-bold">{actionCount}</span>
            }
            return <span className="ml-1.5 text-xs px-1.5 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400">{globalTemplates.length}</span>
          })()}
        </button>
      </div>

      {/* ═══ Browse Global Templates Tab (read-only, copy to org) ═══ */}
      {activeTab === 'global' && (
        <>
          {copySuccess && (
            <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-400 text-sm flex items-center gap-2">
              <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              {copySuccess}
            </div>
          )}

          {globalTemplates.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center">
              <svg className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
              </svg>
              <p className="text-gray-500 dark:text-gray-400 mb-2">No global templates available</p>
              <p className="text-sm text-gray-400 dark:text-gray-500">Global templates are created by SSA Platform Admins and will appear here when available</p>
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-700/50">
                    <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Title</th>
                    <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Domain</th>
                    <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Frameworks</th>
                    <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Org Status</th>
                    <th className="px-4 py-2.5 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {globalTemplates.map(gt => {
                    // Determine org adoption status
                    const orgCopy = templates.find(t => t.source_global_template_id === gt.id)
                    const isAdopted = !!orgCopy
                    const isCustomized = isAdopted && orgCopy.org_edited

                    let statusTag = null
                    if (isCustomized) {
                      statusTag = <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">Customized</span>
                    } else if (isAdopted) {
                      statusTag = <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">Adopted</span>
                    } else {
                      statusTag = <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">New</span>
                    }

                    return (
                      <tr key={gt.id} className="border-t border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                        <td className="px-4 py-2.5 text-sm font-medium text-gray-900 dark:text-white max-w-[220px] truncate">{gt.title}</td>
                        <td className="px-4 py-2.5 text-sm text-gray-500 dark:text-gray-400 max-w-[180px] truncate">{gt.domain}</td>
                        <td className="px-4 py-2.5">
                          <div className="flex flex-wrap gap-1">
                            {(gt.framework_tags || []).slice(0, 3).map(tag => (
                              <span key={tag} className="text-xs px-1.5 py-0.5 rounded bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">{tag}</span>
                            ))}
                            {(gt.framework_tags || []).length > 3 && (
                              <span className="text-xs text-gray-400">+{gt.framework_tags.length - 3}</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-2.5">{statusTag}</td>
                        <td className="px-4 py-2.5 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => setViewingGlobalTemplate(gt)}
                              className="p-1 rounded text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                              title="Preview"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                            </button>
                            {!isReadOnly && (
                              <button
                                onClick={() => handleCopyGlobalToOrg(gt)}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors"
                                title={isAdopted ? 'Copy another version to org library' : 'Add to org library'}
                              >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                </svg>
                                {isAdopted ? 'Copy' : 'Adopt'}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Global Template View Modal (read-only) */}
          {viewingGlobalTemplate && (
            <GlobalTemplatePolicyPreview
              template={viewingGlobalTemplate}
              versionHistory={[]}
              onClose={() => setViewingGlobalTemplate(null)}
              onDownloadDocx={async () => {
                if (checkDemoBlock('Policy downloads')) return
                try {
                  await exportPolicyAsDocx(viewingGlobalTemplate, [], { name: currentOrg?.name || 'Organization' }, null, currentOrg?.name || 'Organization')
                } catch (err) { console.error('DOCX export failed:', err) }
              }}
              onDownloadPdf={async () => {
                if (checkDemoBlock('Policy downloads')) return
                try {
                  if (viewingGlobalTemplate.structured_content) {
                    await exportStructuredPolicyToPdf(viewingGlobalTemplate, [], { name: currentOrg?.name || 'Organization' }, null, currentOrg?.name)
                  } else {
                    await exportPolicyToPdf({ title: viewingGlobalTemplate.title || viewingGlobalTemplate.domain || 'Policy', elementId: 'global-template-preview-content' })
                  }
                } catch (err) { console.error('PDF export failed:', err) }
              }}
            />
          )}
        </>
      )}

      {/* ═══ Org Templates Tab ═══ */}
      {activeTab === 'org' && <>
      {/* Update notification banner */}
      {pendingUpdates.length > 0 && !bannerDismissed && (
        <div className="mb-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 border-l-4 border-l-amber-400 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <svg className="w-5 h-5 text-amber-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
            </svg>
            <p className="text-sm text-amber-800 dark:text-amber-300">
              <strong>{pendingUpdates.length}</strong> template update{pendingUpdates.length !== 1 ? 's' : ''} available from SSA — Review the changes and choose whether to adopt them.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 ml-4">
            <button
              onClick={() => setShowUpdateReview(true)}
              className="px-3 py-1.5 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-lg transition-colors"
            >
              Review Updates
            </button>
            <button onClick={() => setBannerDismissed(true)} className="text-amber-500 hover:text-amber-700 dark:hover:text-amber-300">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Filter bar */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-6">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex-1 min-w-[200px]">
            <input
              type="text"
              placeholder="Search by title or domain..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
            />
          </div>

          <select
            value={domainFilter}
            onChange={e => setDomainFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
          >
            <option value="">All Domains</option>
            {DOMAIN_OPTIONS.map(d => <option key={d} value={d}>{d}</option>)}
          </select>

          <select
            value={frameworkFilter}
            onChange={e => setFrameworkFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
          >
            <option value="">All Frameworks</option>
            {FRAMEWORK_OPTIONS.map(f => <option key={f} value={f}>{f}</option>)}
          </select>

          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="pl-3 pr-8 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
          >
            <option value="">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
          </select>

          {hasActiveFilters && (
            <button
              onClick={() => { setSearch(''); setDomainFilter(''); setFrameworkFilter(''); setStatusFilter('') }}
              className="text-sm text-primary-600 hover:text-primary-700 dark:text-accent-300 dark:hover:text-accent-200"
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Template count */}
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
        Showing {filtered.length} of {templates.length} template{templates.length !== 1 ? 's' : ''}
      </p>

      {/* Table view */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        {templates.length === 0 ? (
          <div className="p-12 text-center">
            <svg className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="text-gray-500 dark:text-gray-400 mb-2">No policy templates yet</p>
            {isReadOnly ? (
              <p className="text-sm text-gray-400 dark:text-gray-500">Policy templates will appear here once created by an administrator.</p>
            ) : (
              <>
                <p className="text-sm text-gray-400 dark:text-gray-500 mb-4">Create your first template or adopt one from the Global Templates tab</p>
                <button onClick={openCreateModal} className="btn-primary">Create Policy</button>
              </>
            )}
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-gray-500 dark:text-gray-400">No templates match your filters</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-700/50">
                  <th
                    onClick={() => handleSort('title')}
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:text-gray-700 dark:hover:text-gray-300"
                  >
                    Title<SortIndicator field="title" />
                  </th>
                  <th
                    onClick={() => handleSort('domain')}
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:text-gray-700 dark:hover:text-gray-300"
                  >
                    Domain<SortIndicator field="domain" />
                  </th>
                  <th
                    onClick={() => handleSort('status')}
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:text-gray-700 dark:hover:text-gray-300"
                  >
                    Status<SortIndicator field="status" />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Source
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Frameworks
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Industries
                  </th>
                  <th
                    onClick={() => handleSort('updated')}
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:text-gray-700 dark:hover:text-gray-300"
                  >
                    Updated<SortIndicator field="updated" />
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {sorted.map(template => (
                  <tr
                    key={template.id}
                    className="border-t border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
                  >
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white max-w-[240px]">
                      <div className="flex items-center gap-2">
                        <span className="truncate">{template.title}</span>
                        {pendingUpdates.some(u => u.id === template.id) && (
                          <span className="flex-shrink-0 text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 whitespace-nowrap">Update available</span>
                        )}
                        {template.org_edited && (
                          <span className="flex-shrink-0 text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400 whitespace-nowrap">Customized</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 max-w-[180px] truncate">
                      {template.domain}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${
                        template.status === 'published'
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                          : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                      }`}>
                        {template.status === 'published' ? 'Published' : 'Draft'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {template.source_global_template_id && !template.org_edited ? (
                        <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">Global</span>
                      ) : (
                        <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">Custom</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="flex flex-wrap gap-1">
                        {(template.framework_tags || []).slice(0, 3).map(tag => (
                          <span key={tag} className="text-xs px-2 py-0.5 rounded bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 mr-1">
                            {tag}
                          </span>
                        ))}
                        {(template.framework_tags || []).length > 3 && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            +{template.framework_tags.length - 3}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="flex flex-wrap gap-1">
                        {(template.industry_tags || []).slice(0, 2).map(tag => (
                          <span key={tag} className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 mr-1">
                            {tag}
                          </span>
                        ))}
                        {(template.industry_tags || []).length > 2 && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            +{template.industry_tags.length - 2}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
                      {relativeTime(template.updated_at)}
                    </td>
                    <td className="px-4 py-3 text-sm" onClick={e => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        {/* Preview */}
                        <button
                          onClick={() => { setPreviewPolicy(template); setShowPreviewModal(true); setShowPreviewExportMenu(false) }}
                          className="p-1 rounded text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                          title="Preview"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </button>

                        {/* Edit (title, domain, status only) — admin/org_admin only */}
                        {!isReadOnly && <button
                          onClick={() => openEditModal(template)}
                          className="p-1 rounded text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                          title="Edit details"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                          </svg>
                        </button>}

                        {/* Download & Edit */}
                        <button
                          onClick={async () => {
                            if (checkDemoBlock('Policy downloads')) return
                            try {
                              await exportPolicyAsDocx(template, [], { name: currentOrg?.name || 'Organization' }, null, currentOrg?.name || 'Organization')
                              setSuccessMessage('Downloaded. Edit in Word, then use Upload Revision to save.')
                            } catch (err) {
                              console.error('Export failed:', err)
                            }
                          }}
                          className="p-1 rounded text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                          title="Download & Edit"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                          </svg>
                        </button>

                        {/* Upload Revision — admin/org_admin only */}
                        {!isReadOnly && (
                          <button
                            onClick={() => { setUploadRevisionTemplate(template); setShowUploadRevision(true); setUploadFile(null); setUploadChangeSummary('') }}
                            className="p-1 rounded text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                            title="Upload Revision"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
                            </svg>
                          </button>
                        )}

                        {/* Copy to client — admin/org_admin only */}
                        {!isReadOnly && (
                        <div className="relative" ref={showCopyDropdown === template.id ? copyDropdownRef : null}>
                          <button
                            onClick={() => setShowCopyDropdown(showCopyDropdown === template.id ? null : template.id)}
                            className="p-1 rounded text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                            title="Copy to client"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" />
                            </svg>
                          </button>

                          {showCopyDropdown === template.id && (
                            <div className="absolute right-0 mt-1 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-10 max-h-48 overflow-y-auto">
                              {clients.length === 0 ? (
                                <p className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">No clients found</p>
                              ) : (
                                clients.map(c => (
                                  <button
                                    key={c.id}
                                    onClick={() => handleCopyToClient(template.id, c)}
                                    className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                                  >
                                    {c.name}
                                  </button>
                                ))
                              )}
                            </div>
                          )}
                        </div>
                        )}

                        {/* Review Update */}
                        {pendingUpdates.some(u => u.id === template.id) && (
                          <button
                            onClick={() => { setSingleReviewTemplate(pendingUpdates.find(u => u.id === template.id)); setShowUpdateReview(true) }}
                            className="p-1 rounded text-amber-500 hover:text-amber-700 dark:hover:text-amber-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                            title="Review Update"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182" />
                            </svg>
                          </button>
                        )}

                        {/* Delete */}
                        <button
                          onClick={() => setShowDeleteConfirm(template)}
                          className="p-1 rounded text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                          title="Delete"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Modal — Title, Domain, Status only */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {editingTemplate ? 'Edit Policy Details' : 'Create Policy Template'}
              </h2>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSave} className="space-y-4">
              {saveError && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
                  {saveError}
                </div>
              )}

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Title</label>
                <input
                  type="text"
                  value={formTitle}
                  onChange={e => setFormTitle(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="e.g., Acceptable Use Policy v1"
                  required
                />
              </div>

              {/* Domain */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Domain</label>
                <select
                  value={formDomain}
                  onChange={e => setFormDomain(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                >
                  <option value="">Select domain...</option>
                  {DOMAIN_OPTIONS.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700 dark:text-gray-300">
                    <input type="radio" name="status" value="draft" checked={formStatus === 'draft'} onChange={() => setFormStatus('draft')} className="text-primary-600 focus:ring-primary-500" />
                    Draft
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700 dark:text-gray-300">
                    <input type="radio" name="status" value="published" checked={formStatus === 'published'} onChange={() => setFormStatus('published')} className="text-primary-600 focus:ring-primary-500" />
                    Published
                  </label>
                </div>
              </div>

              {/* Footer buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button type="button" onClick={closeModal} className="btn-secondary">Cancel</button>
                <button type="submit" disabled={saving} className="btn-primary">
                  {saving ? 'Saving...' : editingTemplate ? 'Save Changes' : 'Create Policy'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-sm">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Delete Policy</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to delete <span className="font-medium">&ldquo;{showDeleteConfirm.title}&rdquo;</span>? This cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setShowDeleteConfirm(null)} className="btn-secondary">Cancel</button>
              <button
                onClick={() => handleDelete(showDeleteConfirm.id)}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreviewModal && previewPolicy && (
        <GlobalTemplatePolicyPreview
          template={previewPolicy}
          versionHistory={[]}
          onClose={() => { setShowPreviewModal(false); setPreviewPolicy(null); setShowPreviewExportMenu(false) }}
          onDownloadDocx={async () => {
            if (checkDemoBlock('Policy downloads')) return
            try {
              await exportPolicyAsDocx(previewPolicy, [], { name: currentOrg?.name || 'Organization' }, null, currentOrg?.name || 'Organization')
            } catch (err) { console.error('DOCX export failed:', err) }
          }}
          onDownloadPdf={async () => {
            if (checkDemoBlock('Policy downloads')) return
            try {
              if (previewPolicy.structured_content) {
                await exportStructuredPolicyToPdf(previewPolicy, [], { name: currentOrg?.name || 'Organization' }, null, currentOrg?.name)
              } else {
                await exportPolicyToPdf({ title: previewPolicy.title || previewPolicy.domain || 'Policy', elementId: 'global-template-preview-content' })
              }
            } catch (err) { console.error('PDF export failed:', err) }
          }}
        />
      )}

      </>}

      {/* Policy Generation Settings Panel */}
      {showSettings && <PolicySettingsPanel onClose={() => setShowSettings(false)} />}

      {/* Section Editor Panel */}
      {sectionEditorTemplate && (
        <SectionEditorPanel
          template={sectionEditorTemplate}
          onClose={() => setSectionEditorTemplate(null)}
          onSave={() => { setSectionEditorTemplate(null); fetchTemplates() }}
        />
      )}

      {/* Branding Application Modal */}
      {showBrandingModal && orgBrandConfig && (
        <BrandingApplicationModal
          orgBrandConfig={orgBrandConfig}
          orgName={currentOrg?.name}
          templates={templates}
          onClose={() => setShowBrandingModal(false)}
          onComplete={(count) => {
            setShowBrandingModal(false)
            fetchTemplates()
            setSuccessMessage(`Branding applied to ${count} template${count !== 1 ? 's' : ''}`)
          }}
        />
      )}

      {/* Upload Revision Modal */}
      {showUploadRevision && uploadRevisionTemplate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Upload Revised Template</h3>
              <button onClick={() => { setShowUploadRevision(false); setUploadRevisionTemplate(null); setUploadFile(null); setUploadChangeSummary('') }} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Download the template, make your changes in Word, then upload the revised .docx file here.
            </p>
            <div
              className={`border-2 border-dashed rounded-lg p-6 text-center mb-4 cursor-pointer transition-colors ${
                uploadFile ? 'border-primary-400 bg-primary-50 dark:bg-primary-900/20' : 'border-gray-300 dark:border-gray-600 hover:border-primary-400'
              }`}
              onClick={() => document.getElementById('upload-revision-input').click()}
            >
              <input id="upload-revision-input" type="file" accept=".docx" className="hidden" onChange={e => setUploadFile(e.target.files[0] || null)} />
              {uploadFile ? (
                <div>
                  <p className="text-sm font-medium text-primary-700 dark:text-primary-300">{uploadFile.name}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{(uploadFile.size / 1024).toFixed(1)} KB</p>
                </div>
              ) : (
                <div>
                  <svg className="w-8 h-8 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
                  </svg>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Drop your .docx file here or click to browse</p>
                </div>
              )}
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Change summary <span className="text-red-500">*</span></label>
              <textarea value={uploadChangeSummary} onChange={e => setUploadChangeSummary(e.target.value)} rows={3} placeholder="Briefly describe what you changed..." className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm resize-none" />
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => { setShowUploadRevision(false); setUploadRevisionTemplate(null); setUploadFile(null); setUploadChangeSummary('') }} className="btn-secondary">Cancel</button>
              <button onClick={handleUploadRevision} disabled={!uploadFile || !uploadChangeSummary.trim() || uploading} className="btn-primary disabled:opacity-50">
                {uploading ? 'Uploading...' : 'Upload & Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Template Update Review Modal */}
      {showUpdateReview && (
        <TemplateUpdateReviewModal
          templates={singleReviewTemplate ? [singleReviewTemplate] : pendingUpdates}
          onClose={() => { setShowUpdateReview(false); setSingleReviewTemplate(null) }}
          onUpdate={() => { loadPendingUpdates(); fetchTemplates() }}
        />
      )}
    </div>
  )
}

export default PolicyLibrary
